import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const chaveFromSelectedItem = (item) => {
  if (item == null) return null;
  if (typeof item === "string") return item;
  if (typeof item === "object" && item.id != null) return String(item.id);
  return null;
};

/** Mesma cor do título “Dados do Grupo” (`.formTitle` em GruposUsuarios). */
const PERM_LABEL_COLOR = "#1b3a5f";

const selectedChavesSet = (selected) => {
  const set = new Set();
  (selected || []).forEach((item) => {
    const c = chaveFromSelectedItem(item);
    if (c) set.add(c);
  });
  return set;
};

const PermissionsTree = ({
  data = [],
  selected = [],
  onChange,
  showCounters = true,
  /** Layout estilo painel: árvore em coluna flexível (rodapé com contagem fica no card pai). */
  panelLayout = false,
  expandAllToken = 0,
  collapseAllToken = 0,
}) => {
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedSubcats, setExpandedSubcats] = useState({});

  const toggleModule = (modKey) => {
    setExpandedModules((prev) => ({ ...prev, [modKey]: !prev[modKey] }));
  };

  const toggleSubcat = (subcatKey) => {
    setExpandedSubcats((prev) => ({ ...prev, [subcatKey]: !prev[subcatKey] }));
  };

  const chavesSet = selectedChavesSet(selected);
  const isSelected = (chave) => chavesSet.has(chave);

  const handleTogglePermission = (chave) => {
    if (isSelected(chave)) {
      onChange(
        (selected || []).filter((item) => chaveFromSelectedItem(item) !== chave)
      );
    } else {
      onChange([...(selected || []), chave]);
    }
  };

  const allChavesFlat = useMemo(() => {
    const allChaves = [];
    (data || []).forEach((mod) =>
      (mod.subcategorias || []).forEach((sub) =>
        (sub.permissoes || []).forEach((p) => {
          if (p?.chave) allChaves.push(p.chave);
        })
      )
    );
    return [...new Set(allChaves)];
  }, [data]);

  useEffect(() => {
    if (!expandAllToken) return;
    const nextModules = {};
    const nextSubcats = {};
    (data || []).forEach((mod, modIdx) => {
      const modKey = `mod-${modIdx}`;
      nextModules[modKey] = true;
      (mod.subcategorias || []).forEach((_, subIdx) => {
        nextSubcats[`${modKey}-sub-${subIdx}`] = true;
      });
    });
    setExpandedModules(nextModules);
    setExpandedSubcats(nextSubcats);
  }, [data, expandAllToken]);

  useEffect(() => {
    if (!collapseAllToken) return;
    setExpandedModules({});
    setExpandedSubcats({});
  }, [collapseAllToken]);

  const handleSelectAll = () => {
    onChange([...allChavesFlat]);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const toggleSelectAllGlobal = () => {
    if (allChavesFlat.length === 0) return;
    const allMarked = allChavesFlat.every((c) => chavesSet.has(c));
    if (allMarked) handleClearAll();
    else handleSelectAll();
  };

  const getModuleStats = (mod) => {
    let total = 0;
    let selectedCount = 0;
    (mod.subcategorias || []).forEach((sub) =>
      (sub.permissoes || []).forEach((p) => {
        if (p?.chave) {
          total++;
          if (isSelected(p.chave)) selectedCount++;
        }
      })
    );
    return { total, selectedCount };
  };

  const getSubcatStats = (sub) => {
    let total = 0;
    let selectedCount = 0;
    (sub.permissoes || []).forEach((p) => {
      if (p?.chave) {
        total++;
        if (isSelected(p.chave)) selectedCount++;
      }
    });
    return { total, selectedCount };
  };

  const handleToggleModule = (mod) => {
    const stats = getModuleStats(mod);
    const allChaves = [];
    (mod.subcategorias || []).forEach((sub) =>
      (sub.permissoes || []).forEach((p) => {
        if (p?.chave) allChaves.push(p.chave);
      })
    );

    if (stats.selectedCount === stats.total) {
      onChange(
        (selected || []).filter((item) => !allChaves.includes(chaveFromSelectedItem(item)))
      );
    } else {
      onChange([...new Set([...(selected || []), ...allChaves])]);
    }
  };

  const handleToggleSubcat = (sub) => {
    const stats = getSubcatStats(sub);
    const allChaves = (sub.permissoes || []).map((p) => p.chave).filter(Boolean);

    if (stats.selectedCount === stats.total) {
      onChange(
        (selected || []).filter((item) => !allChaves.includes(chaveFromSelectedItem(item)))
      );
    } else {
      onChange([...new Set([...(selected || []), ...allChaves])]);
    }
  };

  const totalGlobal = allChavesFlat.length;
  const selectedGlobalCount = allChavesFlat.filter((c) => chavesSet.has(c)).length;
  const allGlobalChecked = totalGlobal > 0 && selectedGlobalCount === totalGlobal;
  const globalIndeterminate = selectedGlobalCount > 0 && selectedGlobalCount < totalGlobal;

  const checkboxSx = {
    p: 0.5,
    color: "#5f6368",
    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#1a73e8" },
    "& .MuiSvgIcon-root": { fontSize: 20 },
  };

  const treeScrollBox = (
      <Box
        sx={{
          flex: panelLayout ? 1 : undefined,
          maxHeight: panelLayout ? undefined : 400,
          overflowY: panelLayout ? "visible" : "auto",
          border: panelLayout ? "none" : "1px solid #e8eaed",
          borderRadius: panelLayout ? 0 : "8px",
          px: panelLayout ? 2 : 0,
          py: panelLayout ? 1.5 : 0,
          bgcolor: "#fff",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f3f4",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#dadce0",
            borderRadius: "4px",
          },
        }}
      >
        {(data || []).map((mod, modIdx) => {
          const modKey = `mod-${modIdx}`;
          const modStats = getModuleStats(mod);
          const isModExpanded = expandedModules[modKey];
          const isModIndeterminate = modStats.selectedCount > 0 && modStats.selectedCount < modStats.total;
          const isModChecked = modStats.selectedCount === modStats.total && modStats.total > 0;
          const moduloAnterior = (data || [])[modIdx - 1];
          const mudouDeGrupo = modIdx === 0 || (moduloAnterior?.id === "portal") !== (mod.id === "portal");

          return (
            <Box key={modKey}>
              {mudouDeGrupo && (
                <Box sx={{ px: 1.25, py: 0.75, bgcolor: "#18316e", borderRadius: 1, mb: 0.75, mt: modIdx === 0 ? 0 : 1.5 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ color: "#fff", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {mod.id === "portal" ? "Permissões Portal" : "Permissões Planejamento Tributário"}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mb: panelLayout ? 0.75 : 0, borderTop: !panelLayout && modIdx > 0 && !mudouDeGrupo ? "1px solid #e8eaed" : "none" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: panelLayout ? 49 : 44,
                  px: panelLayout ? 1.25 : 1,
                  py: panelLayout ? 0.875 : 0.5,
                  bgcolor: "#f1f3f4",
                  borderRadius: panelLayout ? 1 : 0,
                  borderBottom: !panelLayout && isModExpanded ? "1px solid #e8eaed" : "none",
                  "&:hover": { bgcolor: "#e8eaed" },
                }}
              >
                <IconButton size="small" aria-label={isModExpanded ? "Recolher" : "Expandir"} onClick={() => toggleModule(modKey)} sx={{ p: 0.5 }}>
                  {isModExpanded ? <ExpandMoreIcon sx={{ fontSize: 22, color: "#1976d2" }} /> : <ChevronRightIcon sx={{ fontSize: 22, color: "#1976d2" }} />}
                </IconButton>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isModChecked}
                      indeterminate={isModIndeterminate}
                      onChange={() => handleToggleModule(mod)}
                      size="small"
                      sx={checkboxSx}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem", color: PERM_LABEL_COLOR }}>
                        {mod.nome || mod.modulo || mod.categoria || `Módulo ${modIdx + 1}`}
                      </Typography>
                      {showCounters ? (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            px: 1.25,
                            py: 0.35,
                            bgcolor: "#e3f2fd",
                            color: "#1976d2",
                            borderRadius: "16px",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            ml: "auto",
                          }}
                        >
                          {`${modStats.selectedCount} de ${modStats.total} ${modStats.total === 1 ? "permissão" : "permissões"}`}
                        </Typography>
                      ) : null}
                    </Box>
                  }
                  sx={{ flex: 1, m: 0, alignItems: "center", "& .MuiFormControlLabel-label": { width: "100%" } }}
                />
              </Box>

              <Collapse in={isModExpanded} timeout="auto" unmountOnExit={false}>
                <Box sx={{ pl: panelLayout ? 1.5 : 3.5, borderLeft: panelLayout ? "1px solid #e8e8e8" : "none", ml: panelLayout ? 0.5 : 0 }}>
                  {(mod.subcategorias || []).map((sub, subIdx) => {
                    const subcatKey = `${modKey}-sub-${subIdx}`;
                    const subStats = getSubcatStats(sub);
                    const isSubExpanded = expandedSubcats[subcatKey];
                    const isSubIndeterminate = subStats.selectedCount > 0 && subStats.selectedCount < subStats.total;
                    const isSubChecked = subStats.selectedCount === subStats.total && subStats.total > 0;

                    return (
                      <Box key={subcatKey}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            minHeight: panelLayout ? 43 : 40,
                            borderBottom: panelLayout ? "none" : "1px solid #e8eaed",
                            bgcolor: "#f8f9fa",
                            borderRadius: panelLayout ? 1 : 0,
                            pl: 0,
                            py: panelLayout ? 0.625 : 0.25,
                            px: panelLayout ? 1 : 0,
                            mb: panelLayout ? 0.75 : 0,
                            "&:hover": { bgcolor: "#f0f2f5" },
                          }}
                        >
                          <IconButton size="small" aria-label={isSubExpanded ? "Recolher" : "Expandir"} onClick={() => toggleSubcat(subcatKey)} sx={{ p: 0.5 }}>
                            {isSubExpanded ? <ExpandMoreIcon sx={{ fontSize: 20, color: "#1976d2" }} /> : <ChevronRightIcon sx={{ fontSize: 20, color: "#1976d2" }} />}
                          </IconButton>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSubChecked}
                                indeterminate={isSubIndeterminate}
                                onChange={() => handleToggleSubcat(sub)}
                                size="small"
                                sx={checkboxSx}
                              />
                            }
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", width: "100%" }}>
                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem", color: PERM_LABEL_COLOR }}>
                                  {sub.nome || sub.subcategoria || sub.categoria || `Subcategoria ${subIdx + 1}`}
                                </Typography>
                                {showCounters ? (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                      px: 1.25,
                                      py: 0.35,
                                      bgcolor: "#e3f2fd",
                                      color: "#1976d2",
                                      borderRadius: "16px",
                                      fontWeight: 500,
                                      fontSize: "0.75rem",
                                      ml: "auto",
                                    }}
                                  >
                                    {`${subStats.selectedCount} de ${subStats.total} ${subStats.total === 1 ? "permissão" : "permissões"}`}
                                  </Typography>
                                ) : null}
                              </Box>
                            }
                            sx={{ flex: 1, m: 0, alignItems: "center", "& .MuiFormControlLabel-label": { width: "100%" } }}
                          />
                        </Box>

                        <Collapse in={isSubExpanded} timeout="auto" unmountOnExit={false}>
                          <Box sx={{ pl: 3.5 }}>
                            {(sub.permissoes || []).filter((p) => p?.chave).map((perm, permIdx, arr) => {
                              const isLastLeaf = permIdx === arr.length - 1;
                              return (
                                <Box
                                  key={`${subcatKey}-perm-${perm.chave}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    borderBottom: isLastLeaf ? "none" : "1px solid #f1f3f4",
                                    py: 0.5,
                                    pl: 0,
                                  }}
                                >
                                  <Box sx={{ width: 40, flexShrink: 0 }} aria-hidden />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isSelected(perm.chave)}
                                        onChange={() => handleTogglePermission(perm.chave)}
                                        size="small"
                                        sx={checkboxSx}
                                      />
                                    }
                                    label={
                                      <Typography variant="body2" fontWeight={400} sx={{ fontSize: "0.8125rem", color: PERM_LABEL_COLOR }}>
                                        {perm.descricao || perm.chave}
                                      </Typography>
                                    }
                                    sx={{ flex: 1, m: 0, alignItems: "center" }}
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
              </Box>
            </Box>
          );
        })}
      </Box>
  );

  if (panelLayout) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {treeScrollBox}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {selectedGlobalCount} permissões selecionadas
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={handleSelectAll} sx={{ textTransform: "none" }}>
            Selecionar Todas
          </Button>
          <Button size="small" onClick={handleClearAll} sx={{ textTransform: "none" }}>
            Limpar Todas
          </Button>
        </Box>
      </Box>

      {treeScrollBox}
    </Box>
  );
};

export default PermissionsTree;
